import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../../environments/environment';

export interface NotificacaoSignalR {
  Id: string;
  Lido: boolean;
  TipoNotificacao: {
    Id: string;
    Codigo: number;
    Descricao: string;
  };
  DataLido?: string;
  UsuarioNotificacao?: {
    id: string;
    name_FirstName: string;
    name_MiddleName: string;
    name_LastName: string;
    email_Address: string;
    document_Number: string;
    document_Type?: number;
    responsibleAnalyst?: boolean;
    ativo?: boolean;
    usuarioFake?: boolean;
    employeeName: string;
  };
  UsuarioNotificado?: {
    id: string;
    name_FirstName: string;
    name_MiddleName: string;
    name_LastName: string;
    email_Address: string;
    document_Number: string;
    document_Type?: number;
    responsibleAnalyst?: boolean;
    ativo?: boolean;
    usuarioFake?: boolean;
    employeeName: string;
  };
  createdDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignalRNotificacaoService {
  private hubConnection: HubConnection;
  private connectionStateSubject = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private notificacoesSubject = new BehaviorSubject<NotificacaoSignalR[]>([]);
  private novaNotificacaoSubject = new BehaviorSubject<NotificacaoSignalR | null>(null);

  public connectionState$ = this.connectionStateSubject.asObservable();
  public notificacoes$ = this.notificacoesSubject.asObservable();
  public novaNotificacao$ = this.novaNotificacaoSubject.asObservable();

  private hubUrl = environment.hubUrl || 'https://localhost:7002';

  constructor() {
    // Conexão será criada apenas quando necessário
  }

  private setupEventHandlers(): void {
    // Evento quando recebe todas as notificações
    this.hubConnection.on('ReceberNotificacoes', (notificacoes: NotificacaoSignalR[]) => {
      this.notificacoesSubject.next(notificacoes);
    });

    // Evento quando recebe uma nova notificação
    this.hubConnection.on('NovaNotificacao', (notificacao: NotificacaoSignalR) => {
      this.novaNotificacaoSubject.next(notificacao);
      
      // Adiciona à lista de notificações (verificar se não é duplicada)
      const notificacoesAtuais = this.notificacoesSubject.value;
      const jaExiste = notificacoesAtuais.find(n => n.Id === notificacao.Id);
      
      if (!jaExiste) {
        this.notificacoesSubject.next([notificacao, ...notificacoesAtuais]);
      } 
    });

    // Evento quando uma notificação é marcada como lida
    this.hubConnection.on('NotificacaoMarcadaComoLida', (notificacaoId: string) => {
      const notificacoes = this.notificacoesSubject.value;
      const notificacao = notificacoes.find(n => n.Id === notificacaoId);
      if (notificacao) {
        notificacao.Lido = true;
        this.notificacoesSubject.next([...notificacoes]);
      }
    });

    // Evento de erro de conexão
    this.hubConnection.on('ErroConexao', (erro: string) => {
      // Erro silencioso
    });

    // Monitora mudanças no estado da conexão
    this.hubConnection.onclose((error) => {
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
    });

    this.hubConnection.onreconnecting((error) => {
      this.connectionStateSubject.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.connectionStateSubject.next(HubConnectionState.Connected);
    });
  }

  async conectar(usuarioId: string, token?: string): Promise<void> {
    try {
      // Se já estiver conectado, não faz nada
      if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
        return;
      }

      // Para qualquer conexão existente primeiro
      if (this.hubConnection) {
        try {
          await this.hubConnection.stop();
        } catch (stopError) {
          console.error("Erro ao parar conexão anterior:", stopError);
        }
      }

      // Cria nova conexão
      this.criarConexao();

      // Configura handlers antes de conectar (não duplicar com setupEventHandlers)
      // Os handlers de conexão já estão em setupEventHandlers()

      // Inicia a conexão
      await this.hubConnection.start();

      this.connectionStateSubject.next(HubConnectionState.Connected);

      // Registra o usuário no hub
      await this.hubConnection.invoke('RegistrarUsuario', usuarioId, token || '');

    } catch (error) {
      console.error('Erro ao conectar SignalR:', error);
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
      throw error;
    }
  }

  private criarConexao(): void {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${this.hubUrl}/notificacaoHub`, {
        withCredentials: true,
        transport: 4 // LongPolling como fallback mais estável
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(1) // Info level
      .build();

    this.setupEventHandlers();
  }

  async desconectar(): Promise<void> {
    try {
      if (this.hubConnection && 
          (this.hubConnection.state === HubConnectionState.Connected || 
           this.hubConnection.state === HubConnectionState.Connecting)) {
        await this.hubConnection.stop();
        this.connectionStateSubject.next(HubConnectionState.Disconnected);
      }
    } catch (error) {
      console.error('Erro ao desconectar SignalR:', error);
      // Força o estado para desconectado mesmo com erro
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
    }
  }

  async verificarNotificacoes(usuarioId: string, token?: string): Promise<void> {
    try {
      if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
        await this.hubConnection.invoke('VerificarNotificacoes', usuarioId, token);
      }
    } catch (error) {
      console.error('Erro ao verificar notificações:', error);
    }
  }

  async marcarComoLida(usuarioId: string, notificacaoId: number): Promise<void> {
    try {
      if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
        await this.hubConnection.invoke('MarcarComoLida', usuarioId, notificacaoId);
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  }

  getConnectionState(): HubConnectionState {
    return this.hubConnection ? this.hubConnection.state : HubConnectionState.Disconnected;
  }

  isConnected(): boolean {
    return this.hubConnection && this.hubConnection.state === HubConnectionState.Connected;
  }

  async verificarDisponibilidade(): Promise<boolean> {
    try {
      // Tenta fazer uma requisição simples para verificar se o hub está disponível
      const response = await fetch(`${this.hubUrl}/notificacaoHub/negotiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const isAvailable = response.ok;
      return isAvailable;
    } catch (error) {
      return false;
    }
  }
}
